'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { FfbbsiteBlockJobs } from '@/lib/directus';
import TitleH1 from '../common/TitleH1';
import initializeMeiliClient from '@/lib/meilisearch/client';
import { AppConfig } from '@/utils/AppConfig';
import ColoredBall from '@/components/icon/ColoredBall';

let msSearch: ((q: string) => Promise<StructureSuggestion[]>) | null = null;

async function getOrganismeSearch(): Promise<(q: string) => Promise<StructureSuggestion[]>> {
  if (msSearch) return msSearch;

  const searchClient = await initializeMeiliClient();
  const indexName = AppConfig.searchIndexOrganismes;

  msSearch = async (q: string) => {
    const search = searchClient.search as unknown as (
      requests: { indexName: string; params: Record<string, unknown> }[],
    ) => Promise<{ results: { hits?: StructureSuggestion[] }[] }>;
    const { results } = await search([
      {
        indexName,
        params: {
          query: q,
          attributesToRetrieve: [
            'id',
            'code',
            'nom',
            'nomClubPro',
            'logo',
            'commune',
            'communeClubPro',
          ],
          hitsPerPage: 15,
        },
      },
    ]);
    return results[0]?.hits ?? [];
  };

  return msSearch;
}

interface Props {
  blockProps: Omit<FfbbsiteBlockJobs, 'code'>;
  blockId: string;
}

type Option = { id: number | string; name: string };

type Choice = { text: string; value: string };

type Options = {
  contracts: Option[];
  studies: Option[];
  authors: Option[];
  degrees: Choice[];
  contract_specificities: Choice[];
  categories: Choice[];
};

type OrganismeCommune = { libelle?: string | null; codePostal?: string | null };

type StructureSuggestion = {
  id: string;
  code?: string | null;
  nom: string;
  nomClubPro?: string | null;
  logo?: string | { id?: string | null } | null;
  commune?: OrganismeCommune | null;
  communeClubPro?: OrganismeCommune | null;
};

const logoId = (logo: StructureSuggestion['logo']): string | null => {
  if (!logo) return null;
  if (typeof logo === 'string') return logo;
  return logo.id ?? null;
};

type FormData = {
  title: string;
  reference: string;
  structure: string;
  structure_linked: string;
  city: string;
  postal_code: string;
  openings: string;
  contract_type: string;
  contract_duration: string;
  category: string;
  degree: string[];
  studies: string;
  contract_specificities: string[];
  weekly_hours: string;
  job_start: string;
  salary: string;
  job_description: string;
  asset: string;
  terms_contact: string;
  mail: string;
  phone: string;
  author: string;
};

const INITIAL_FORM: FormData = {
  title: '',
  reference: '',
  structure: '',
  structure_linked: '',
  city: '',
  postal_code: '',
  openings: '',
  contract_type: '',
  contract_duration: '',
  category: '',
  degree: [],
  studies: '',
  contract_specificities: [],
  weekly_hours: '',
  job_start: '',
  salary: '',
  job_description: '',
  asset: '',
  terms_contact: '',
  mail: '',
  phone: '',
  author: '',
};

export function JobSubscriptionForm({ blockProps, blockId }: Props) {
  const [unlocked, setUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [verifiedCode, setVerifiedCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [codeChecking, setCodeChecking] = useState(false);

  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [options, setOptions] = useState<Options | null>(null);
  const [optionsError, setOptionsError] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [preselectedStructure, setPreselectedStructure] = useState<StructureSuggestion | null>(
    null,
  );

  useEffect(() => {
    fetch('/api/job-options')
      .then((r) => r.json())
      .then((data) => {
        if (data.message) throw new Error(data.message);
        setOptions(data);
      })
      .catch(() => setOptionsError(true));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev: FormData) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const contractName = (id: string) =>
    options?.contracts.find((c) => String(c.id) === id)?.name ?? '';
  const isCdi = contractName(form.contract_type).toLowerCase().includes('cdi');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInProgress(true);
    setSuccessMessage('');
    setErrorMessage('');

    if (!form.mail.trim() && !form.phone.trim()) {
      setErrorMessage('Veuillez renseigner un email ou un téléphone.');
      setInProgress(false);
      return;
    }

    const toIdOrString = (v: string) => (/^\d+$/.test(v) ? Number(v) : v || undefined);

    // "Nombre d'heures" dans contract_specificities
    const termsContact =
      form.contract_specificities.includes('part-time') && form.weekly_hours
        ? `${form.terms_contact || ''}<p>Nombre d'heures : ${form.weekly_hours}</p>`
        : form.terms_contact;

    const payload: Record<string, unknown> = {
      ...form,
      ...(blockId && verifiedCode ? { _blockId: blockId, _code: verifiedCode } : {}),
      type: 'structure',
      contract_type: form.contract_type ? toIdOrString(form.contract_type) : undefined,
      studies: form.studies ? toIdOrString(form.studies) : undefined,
      structure: form.structure || undefined,
      structure_linked: form.structure_linked || undefined,
      city: form.city || undefined,
      postal_code: form.postal_code || undefined,
      reference: form.reference || undefined,
      openings: form.openings ? Number(form.openings) : undefined,
      salary: form.salary || undefined,
      contract_duration: form.contract_duration || undefined,
      job_start: form.job_start || undefined,
      asset: form.asset || undefined,
      terms_contact: termsContact || undefined,
      mail: form.mail || undefined,
      phone: form.phone || undefined,
      author: form.author ? toIdOrString(form.author) : undefined,
    };

    try {
      const res = await fetch('/api/job-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.message !== 'success') {
        console.error('[JobSubscriptionForm] server error:', data);
        throw new Error(data.message || 'error');
      }
      setForm(INITIAL_FORM);
      setSuccessMessage('Votre offre a bien été soumise.');
    } catch {
      setErrorMessage('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setInProgress(false);
    }
  };

  const verifyCode = async (codeArg?: string) => {
    const code = (codeArg ?? codeInput).trim();
    if (!code) return;
    setCodeChecking(true);
    setCodeError(false);
    try {
      const res = await fetch('/api/job-verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blockId, code }),
      });
      if (res.ok) {
        const data = await res.json();
        setVerifiedCode(code);
        setUnlocked(true);
        if (data.organisme) {
          const o = data.organisme as StructureSuggestion;
          setPreselectedStructure(o);
          setForm((prev: FormData) => ({ ...prev, structure: o.id }));
        }
      } else {
        setCodeInput(code);
        setCodeError(true);
      }
    } catch {
      setCodeInput(code);
      setCodeError(true);
    } finally {
      setCodeChecking(false);
    }
  };

  const autoVerifyAttempted = useRef(false);

  useEffect(() => {
    if (autoVerifyAttempted.current) return;
    const { search, hash } = window.location;
    let code = new URLSearchParams(search).get('code');
    if (!code && hash.includes('?')) {
      code = new URLSearchParams(hash.slice(hash.indexOf('?') + 1)).get('code');
    }
    if (!code) return;
    autoVerifyAttempted.current = true;
    verifyCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex justify-center p-0 lg:min-h-[700px] lg:p-20">
      <div className="z-10 w-full lg:w-[800px]">
        {blockProps.title && (
          <TitleH1 styles="mb-7" size="medium" color="black">
            {blockProps.title}
          </TitleH1>
        )}

        {!unlocked && (
          <div className="flex flex-col items-center gap-4 py-12">
            <p className="font-medium">
              Veuillez saisir le code d&apos;accès pour accéder au formulaire.
            </p>
            <div className="flex w-full max-w-xs gap-2">
              <input
                className={`input input-bordered flex-1 ${codeError ? 'input-error' : ''}`}
                type="password"
                placeholder="Code d'accès"
                value={codeInput}
                disabled={codeChecking}
                onChange={(e) => {
                  setCodeInput(e.target.value);
                  setCodeError(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    verifyCode();
                  }
                }}
              />
              <PillButton
                label={codeChecking ? '...' : 'Valider'}
                noMargin={true}
                disabled={codeChecking}
                onClick={() => verifyCode()}
              />
            </div>
            {codeError && <p className="text-sm text-error">Code incorrect.</p>}
          </div>
        )}

        {unlocked && optionsError && (
          <div role="alert" className="alert alert-error mb-4">
            <span>Impossible de charger les options. Veuillez recharger la page.</span>
          </div>
        )}

        {unlocked && (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Titre du poste" required className="md:col-span-2">
              <input
                className="input input-bordered w-full"
                name="title"
                value={form.title}
                onChange={handleChange}
                required
              />
            </Field>

            <StructureComboField
              label="Structure qui dépose l’offre"
              value={form.structure}
              onChange={(v) => setForm((prev: FormData) => ({ ...prev, structure: v }))}
              reset={!form.structure}
              preselected={preselectedStructure}
              readOnly={!!preselectedStructure}
              required
            />

            <StructureComboField
              label="Club / structure qui recrute"
              value={form.structure_linked}
              onChange={(v) => setForm((prev: FormData) => ({ ...prev, structure_linked: v }))}
              onSelect={(item) => {
                const commune = item.commune || item.communeClubPro;
                if (commune && (commune.libelle || commune.codePostal)) {
                  setForm((prev: FormData) => ({
                    ...prev,
                    city: commune.libelle || '',
                    postal_code: commune.codePostal || '',
                  }));
                }
              }}
              reset={!form.structure_linked}
              displayCode
              required
            />

            <CityField
              city={form.city}
              postalCode={form.postal_code}
              onChange={(city, postalCode) =>
                setForm((prev: FormData) => ({ ...prev, city, postal_code: postalCode }))
              }
              postalRequired
            />

            <Field label="Postes disponibles">
              <input
                className="input input-bordered w-full"
                type="number"
                name="openings"
                min="1"
                value={form.openings}
                onChange={handleChange}
              />
            </Field>

            <ComboField
              label="Type de Contrat"
              options={options?.contracts ?? []}
              value={form.contract_type}
              onChange={(v) => {
                const cdi = contractName(v).toLowerCase().includes('cdi');
                setForm((prev: FormData) => {
                  const prevCdi = contractName(prev.contract_type).toLowerCase().includes('cdi');
                  return {
                    ...prev,
                    contract_type: v,
                    contract_duration: cdi ? 'Indéterminée' : prevCdi ? '' : prev.contract_duration,
                  };
                });
              }}
              required
            />

            <Field label="Durée du contrat">
              <input
                className={`input input-bordered w-full ${
                  isCdi ? 'cursor-not-allowed bg-base-200 text-base-content/70' : ''
                }`}
                name="contract_duration"
                value={form.contract_duration}
                onChange={handleChange}
                disabled={isCdi}
              />
            </Field>

            <ComboField
              label="Famille"
              options={(options?.categories ?? []).map((c: Choice) => ({
                id: c.value,
                name: c.text,
              }))}
              value={form.category}
              onChange={(v) => setForm((prev: FormData) => ({ ...prev, category: v }))}
              required
            />

            <DegreeField
              choices={options?.degrees ?? []}
              values={form.degree}
              onChange={(v) => setForm((prev: FormData) => ({ ...prev, degree: v }))}
            />

            <TagsComboField
              label="Spécificité(s) du poste"
              suggestions={options?.contract_specificities ?? []}
              values={form.contract_specificities}
              onChange={(v) =>
                setForm((prev: FormData) => ({ ...prev, contract_specificities: v }))
              }
              required
            />

            {form.contract_specificities.includes('part-time') && (
              <Field label="Nombre d'heures" required>
                <input
                  className="input input-bordered w-full"
                  type="number"
                  name="weekly_hours"
                  min="1"
                  placeholder="Nombre d'heures recherchées"
                  value={form.weekly_hours}
                  onChange={handleChange}
                  required
                />
              </Field>
            )}

            <Field label="Date de début" required>
              <input
                className="input input-bordered w-full"
                type="date"
                name="job_start"
                value={form.job_start}
                onChange={handleChange}
                required
              />
            </Field>

            <Field label="Salaire / GROUPE CCNS">
              <input
                className="input input-bordered w-full"
                name="salary"
                value={form.salary}
                onChange={handleChange}
              />
            </Field>

            <Field label="Description du poste" className="md:col-span-2">
              <textarea
                className="textarea textarea-bordered w-full"
                name="job_description"
                rows={5}
                value={form.job_description}
                onChange={handleChange}
              />
            </Field>
            {/* Désactivé pour le moment https://app.clickup.com/t/86c9ggc40 */}
            {/* <AssetField
              value={form.asset}
              onChange={(v) => setForm((prev: FormData) => ({ ...prev, asset: v }))}
              authContext={verifiedCode ? { blockId, code: verifiedCode } : undefined}
            /> */}

            {/* <Field label="Modalités & Contact" className="md:col-span-2">
              <RichTextField
                value={form.terms_contact}
                onChange={(v) => setForm((prev: FormData) => ({ ...prev, terms_contact: v }))}
              />
            </Field> */}

            <Field label="E-mail">
              <input
                className="input input-bordered w-full"
                type="email"
                name="mail"
                value={form.mail}
                onChange={handleChange}
              />
            </Field>

            <Field label="Téléphone">
              <input
                className="input input-bordered w-full"
                type="tel"
                name="phone"
                pattern="[\d\s()+.\-]{6,20}"
                title="Numéro de téléphone valide"
                value={form.phone}
                onChange={handleChange}
              />
            </Field>

            <p className="-mt-2 text-sm text-base-content/70 md:col-span-2">
              Veuillez renseigner au moins un moyen de contact (email ou téléphone).
            </p>

            <div className="mt-2 md:col-span-2">
              <PillButton
                type="submit"
                disabled={inProgress}
                label={inProgress ? 'Envoi en cours...' : "Soumettre l'offre"}
              />
            </div>
          </form>
        )}

        {errorMessage && (
          <div role="alert" className="alert alert-error my-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 shrink-0 stroke-current"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{errorMessage}</span>
          </div>
        )}

        {successMessage && (
          <div role="alert" className="alert alert-success my-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 shrink-0 stroke-current"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{successMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function PillButton({
  label,
  onClick,
  disabled,
  noMargin,
  type = 'button',
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  noMargin?: boolean;
  type?: 'button' | 'submit';
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${noMargin ? '' : 'my-6'}inline-flex h-[50px] w-full items-center justify-center gap-2.5 rounded-[100px] bg-indigo-950 px-[25px] py-2.5 text-white hover:bg-[#18153c] disabled:opacity-50 lg:w-fit`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {label}
      {hovered ? <ColoredBall animated /> : <span className="w-2" />}
    </button>
  );
}

type CommuneSuggestion = { nom: string; codesPostaux: string[] };

function CityField({
  city,
  postalCode,
  onChange,
  postalRequired = false,
}: {
  city: string;
  postalCode: string;
  onChange: (city: string, postalCode: string) => void;
  postalRequired?: boolean;
}) {
  const [input, setInput] = useState(city);
  const [suggestions, setSuggestions] = useState<CommuneSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInput(city);
  }, [city]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInput(v);
    onChange(v, postalCode);
    if (debounce.current) clearTimeout(debounce.current);
    if (v.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(v)}&fields=nom,codesPostaux&limit=8&boost=population`,
        );
        const data: CommuneSuggestion[] = await res.json();
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch {}
    }, 300);
  };

  const select = (item: CommuneSuggestion) => {
    const cp = item.codesPostaux[0] ?? '';
    setInput(item.nom);
    onChange(item.nom, cp);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <>
      <Field label="Ville">
        <div className="relative" ref={ref}>
          <input
            className="input input-bordered w-full"
            value={input}
            onChange={handleInput}
            onFocus={() => suggestions.length > 0 && setOpen(true)}
            placeholder="Rechercher une commune..."
            autoComplete="off"
          />
          {open && (
            <ul
              role="listbox"
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-base-300 bg-base-100 shadow-lg"
            >
              {suggestions.map((item: CommuneSuggestion) => (
                <li
                  role="option"
                  aria-selected={false}
                  key={`${item.nom}-${item.codesPostaux[0]}`}
                  className="flex cursor-pointer justify-between px-4 py-2 hover:bg-base-200"
                  onMouseDown={() => select(item)}
                >
                  <span>{item.nom}</span>
                  <span className="text-sm text-gray-400">{item.codesPostaux[0]}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Field>

      <Field label="Code postal" required={postalRequired}>
        <input
          className="input input-bordered w-full"
          value={postalCode}
          onChange={(e) => onChange(city, e.target.value)}
          placeholder="Code postal"
          required={postalRequired}
        />
      </Field>
    </>
  );
}

function StructureComboField({
  label,
  value,
  onChange,
  reset,
  displayCode = false,
  required = false,
  preselected,
  readOnly = false,
  onSelect,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  reset: boolean;
  displayCode?: boolean;
  required?: boolean;
  preselected?: StructureSuggestion | null;
  readOnly?: boolean;
  onSelect?: (item: StructureSuggestion) => void;
}) {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<StructureSuggestion[]>([]);
  const [selected, setSelected] = useState<StructureSuggestion | null>(null);
  const [open, setOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reset) {
      setInput('');
      setSelected(null);
    }
  }, [reset]);

  useEffect(() => {
    if (!preselected) return;
    const label = displayCode
      ? (preselected.code ?? '')
      : preselected.nomClubPro || preselected.nom || '';
    setInput(label);
    setSelected(preselected);
  }, [preselected, displayCode]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInput(v);
    onChange('');
    setSelected(null);
    if (debounce.current) clearTimeout(debounce.current);
    if (v.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const search = await getOrganismeSearch();
        const data = await search(v);
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch {}
    }, 300);
  };

  const displayLabel = (item: StructureSuggestion) =>
    displayCode ? (item.code ?? '') : item.nomClubPro || item.nom || '';

  const select = (item: StructureSuggestion) => {
    setInput(displayLabel(item));
    setSelected(item);
    onChange(item.id);
    onSelect?.(item);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <Field label={label} required={required}>
      <div className="relative" ref={ref}>
        <input
          className={`input input-bordered w-full${
            readOnly ? 'cursor-not-allowed bg-base-200 text-base-content/70' : ''
          }`}
          value={input}
          onChange={handleInput}
          onFocus={() => !readOnly && suggestions.length > 0 && setOpen(true)}
          placeholder={
            displayCode ? "Rechercher par N° d'affiliation..." : 'Rechercher une structure...'
          }
          autoComplete="off"
          readOnly={readOnly}
        />
        {selected && displayCode && (
          <div className="mt-2 flex items-center gap-3">
            {logoId(selected.logo) && (
              <Image
                src={`${AppConfig.directusPublicUrl}/assets/${logoId(selected.logo)}`}
                alt=""
                width={32}
                height={32}
                className="h-8 w-8 object-contain"
              />
            )}
            <span className="text-sm font-medium">{selected.nomClubPro || selected.nom}</span>
          </div>
        )}
        {open && (
          <ul
            role="listbox"
            className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-base-300 bg-base-100 shadow-lg"
          >
            {suggestions.map((item: StructureSuggestion) => (
              <li
                role="option"
                aria-selected={false}
                key={item.id}
                className="cursor-pointer px-4 py-2 hover:bg-base-200"
                onMouseDown={() => select(item)}
              >
                {displayCode ? (
                  <>
                    <span className="font-medium">{item.code}</span>
                    <span className="ml-2 text-sm text-gray-400">
                      {item.nomClubPro || item.nom}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-medium">{item.nomClubPro || item.nom}</span>
                    {item.nomClubPro && item.nom && (
                      <span className="ml-2 text-sm text-gray-400">{item.nom}</span>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
}

function Field({
  label,
  children,
  required,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  className?: string;
}) {
  return (
    <div className={`form-control w-full ${className}`}>
      <label className="label">
        <span className="label-text font-medium">
          {label}
          {required && ' *'}
        </span>
      </label>
      {children}
    </div>
  );
}

function ComboField({
  label,
  options,
  value,
  onChange,
  collection,
  authContext,
  required = false,
}: {
  label: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  collection?: string;
  authContext?: { blockId: string; code: string };
  required?: boolean;
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value) setInput('');
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = input
    ? options.filter((o) => o.name?.toLowerCase().includes(input.toLowerCase()))
    : options;

  const exactMatch = options.find((o) => o.name?.toLowerCase() === input.toLowerCase());

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInput(v);
    onChange('');
    setAddError(false);
    setOpen(true);
  };

  const select = (o: Option) => {
    setInput(o.name ?? '');
    onChange(String(o.id));
    setOpen(false);
  };

  const addNew = async () => {
    if (!collection || !input) return;
    setAdding(true);
    setAddError(false);
    setOpen(false);
    try {
      const res = await fetch('/api/job-create-option', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collection,
          name: input,
          ...(authContext ? { _blockId: authContext.blockId, _code: authContext.code } : {}),
        }),
      });
      const json = await res.json();
      if (res.ok) {
        onChange(String(json.id));
      } else {
        console.error('[ComboField] addNew error:', json);
        setAddError(true);
      }
    } catch {
      setAddError(true);
    } finally {
      setAdding(false);
    }
  };

  return (
    <Field label={label} required={required}>
      <div className="relative" ref={ref}>
        <input
          className="input input-bordered w-full"
          value={input}
          onChange={handleInput}
          onFocus={() => setOpen(true)}
          autoComplete="off"
          placeholder="Rechercher..."
          disabled={adding}
        />
        {addError && (
          <p className="mt-1 text-xs text-error">Impossible d&apos;ajouter cette valeur.</p>
        )}
        {open && (
          <ul
            role="listbox"
            className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-md border border-base-300 bg-base-100 shadow-lg"
          >
            {filtered.map((o) => (
              <li
                role="option"
                aria-selected={false}
                key={o.id}
                className="cursor-pointer px-4 py-2 hover:bg-base-200"
                onMouseDown={() => select(o)}
              >
                {o.name}
              </li>
            ))}
            {collection && input && !exactMatch && (
              <li
                role="option"
                aria-selected={false}
                className="cursor-pointer border-t border-base-300 px-4 py-2 font-medium text-primary hover:bg-base-200"
                onMouseDown={addNew}
              >
                + Ajouter &laquo;{input}&raquo;
              </li>
            )}
          </ul>
        )}
      </div>
    </Field>
  );
}

function DegreeField({
  choices,
  values,
  onChange,
}: {
  choices: Choice[];
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const regularChoices = choices.filter((c) => c.value !== 'Autre' && c.text !== 'Autre');
  const regularValues = new Set(regularChoices.map((c) => c.value));
  const customValue = values.find((v) => !regularValues.has(v)) ?? '';
  const autreChecked = customValue !== '';

  const toggle = (val: string) => {
    if (values.includes(val)) onChange(values.filter((v) => v !== val));
    else onChange([...values, val]);
  };

  const setAutre = (checked: boolean, text = '') => {
    const withoutCustom = values.filter((v) => regularValues.has(v));
    onChange(checked ? [...withoutCustom, text || 'Autre'] : withoutCustom);
  };

  return (
    <Field label="Diplôme minimum requis" required className="md:col-span-2">
      <div className="grid grid-cols-2 gap-2 pt-1 md:grid-cols-3">
        {regularChoices.map((c) => (
          <label key={c.value} className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={values.includes(c.value)}
              onChange={() => toggle(c.value)}
            />
            <span className="text-sm">{c.text}</span>
          </label>
        ))}
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={autreChecked}
            onChange={(e) => setAutre(e.target.checked)}
          />
          <span className="text-sm">Autre</span>
        </label>
      </div>
      {autreChecked && (
        <input
          className="input input-bordered mt-2 w-full"
          placeholder="Précisez..."
          value={customValue === 'Autre' ? '' : customValue}
          onChange={(e) => setAutre(true, e.target.value)}
        />
      )}
    </Field>
  );
}

function TagsComboField({
  label,
  suggestions,
  values,
  onChange,
  required = false,
}: {
  label: string;
  suggestions: Choice[];
  values: string[];
  onChange: (values: string[]) => void;
  required?: boolean;
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = suggestions.filter(
    (s) => s.text.toLowerCase().includes(input.toLowerCase()) && !values.includes(s.value),
  );

  const addValue = (value: string) => {
    const v = value.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setInput('');
    setOpen(false);
  };

  const remove = (value: string) => onChange(values.filter((v) => v !== value));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (input.trim()) addValue(input);
    } else if (e.key === 'Backspace' && !input && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const exactMatch = suggestions.some((s) => s.text.toLowerCase() === input.toLowerCase());

  const labelFor = (v: string) => suggestions.find((s) => s.value === v)?.text ?? v;

  return (
    <Field label={label} required={required}>
      <div className="relative" ref={ref}>
        <div className="input input-bordered flex h-auto min-h-[2.5rem] w-full flex-wrap gap-1 px-2 py-1">
          {values.map((v) => (
            <span key={v} className="badge badge-neutral shrink-0 gap-1">
              {labelFor(v)}
              <button type="button" onClick={() => remove(v)} aria-label={`Retirer ${labelFor(v)}`}>
                ×
              </button>
            </span>
          ))}
          <input
            className="min-w-[120px] flex-1 bg-transparent text-sm outline-none"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={values.length === 0 ? 'Rechercher ou saisir...' : ''}
            autoComplete="off"
          />
        </div>
        {open && (filtered.length > 0 || (input && !exactMatch)) && (
          <ul
            role="listbox"
            className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-md border border-base-300 bg-base-100 shadow-lg"
          >
            {filtered.map((s) => (
              <li
                role="option"
                aria-selected={false}
                key={s.value}
                className="cursor-pointer px-4 py-2 hover:bg-base-200"
                onMouseDown={() => addValue(s.value)}
              >
                {s.text}
              </li>
            ))}
            {input && !exactMatch && (
              <li
                role="option"
                aria-selected={false}
                className="cursor-pointer border-t border-base-300 px-4 py-2 font-medium text-primary hover:bg-base-200"
                onMouseDown={() => addValue(input)}
              >
                + Ajouter &laquo;{input}&raquo;
              </li>
            )}
          </ul>
        )}
      </div>
    </Field>
  );
}

function AssetField({
  value,
  onChange,
  authContext,
}: {
  value: string;
  onChange: (id: string) => void;
  authContext?: { blockId: string; code: string };
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!value) setFileName('');
  }, [value]);

  const upload = async (file: File) => {
    setUploading(true);
    setError(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (authContext) {
        fd.append('_blockId', authContext.blockId);
        fd.append('_code', authContext.code);
      }
      const res = await fetch('/api/job-asset-upload', { method: 'POST', body: fd });
      const json = await res.json();
      if (!res.ok || !json.id) throw new Error(json.message || 'error');
      onChange(json.id);
      setFileName(file.name);
    } catch {
      setError(true);
      onChange('');
      setFileName('');
      if (inputRef.current) inputRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    onChange('');
    setFileName('');
    setError(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Field label="Ressource">
      <input
        ref={inputRef}
        type="file"
        className="file-input file-input-bordered w-full"
        disabled={uploading}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload(f);
        }}
      />
      {uploading && <p className="mt-1 text-xs text-gray-500">Téléversement en cours...</p>}
      {!uploading && value && (
        <div className="mt-1 flex items-center gap-2 text-xs">
          <span className="truncate">{fileName || 'Fichier ajouté'}</span>
          <button type="button" className="text-error underline" onClick={clear}>
            Retirer
          </button>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-error">Échec du téléversement.</p>}
    </Field>
  );
}

function RichTextField({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (command: string, arg?: string) => {
    document.execCommand(command, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current?.focus();
  };

  const insertLink = () => {
    const url = window.prompt('URL du lien');
    if (url) exec('createLink', url);
  };

  const ToolbarButton = ({
    label,
    title,
    onClick,
  }: {
    label: React.ReactNode;
    title: string;
    onClick: () => void;
  }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="rounded border border-base-300 px-2 py-1 text-sm hover:bg-base-200"
    >
      {label}
    </button>
  );

  return (
    <div className="rounded-md border border-base-300">
      <div className="flex flex-wrap gap-1 border-b border-base-300 bg-base-200 p-1">
        <ToolbarButton label={<b>B</b>} title="Gras" onClick={() => exec('bold')} />
        <ToolbarButton label={<i>I</i>} title="Italique" onClick={() => exec('italic')} />
        <ToolbarButton label={<u>U</u>} title="Souligné" onClick={() => exec('underline')} />
        <ToolbarButton label="H1" title="Titre 1" onClick={() => exec('formatBlock', 'H1')} />
        <ToolbarButton label="H2" title="Titre 2" onClick={() => exec('formatBlock', 'H2')} />
        <ToolbarButton label="H3" title="Titre 3" onClick={() => exec('formatBlock', 'H3')} />
        <ToolbarButton
          label="•"
          title="Liste à puces"
          onClick={() => exec('insertUnorderedList')}
        />
        <ToolbarButton
          label="1."
          title="Liste numérotée"
          onClick={() => exec('insertOrderedList')}
        />
        <ToolbarButton
          label="❝"
          title="Citation"
          onClick={() => exec('formatBlock', 'BLOCKQUOTE')}
        />
        <ToolbarButton label="Lien" title="Lien" onClick={insertLink} />
        <ToolbarButton label="―" title="Séparateur" onClick={() => exec('insertHorizontalRule')} />
        <ToolbarButton label="⌫" title="Effacer le format" onClick={() => exec('removeFormat')} />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange((e.target as HTMLDivElement).innerHTML)}
        className="min-h-[120px] w-full p-3 text-sm outline-none [&_a]:text-primary [&_a]:underline [&_blockquote]:my-2 [&_blockquote]:border-l-4 [&_blockquote]:border-base-300 [&_blockquote]:pl-3 [&_h1]:my-2 [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:my-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:my-2 [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
      />
    </div>
  );
}
